import { useState, useMemo } from 'react';
import { Download, Loader2 } from 'lucide-react';
import PdfPreview from '../components/PdfPreview';
import api from '../services/api';
import ToolPageSections from '../components/ToolPageSections';
import FontSelect from '../components/FontSelect';

// Available fonts for number tracing (print + cursive, matching alphabet tracing)
const FONTS = [
  // Print fonts
  { id: 'writing-step', name: 'Writing Step' },
  { id: 'delius', name: 'Delius' },
  { id: 'edu-sa-hand', name: 'Edu SA Hand' },
  { id: 'edu-vicwa-hand', name: 'Edu VICWA Hand' },
  { id: 'itim', name: 'Itim' },
  { id: 'public-sans', name: 'Public Sans Thin Italic' },
  { id: 'solitreo', name: 'Solitreo' },
  // Cursive fonts
  { id: 'prima-cursive', name: 'Prima Cursive' },
  { id: 'd-nealian', name: "D'Nealian" },
  { id: 'alex-brush', name: 'Alex Brush' },
  { id: 'allura', name: 'Allura' },
  { id: 'cedarville-cursive', name: 'Cedarville Cursive' },
  { id: 'dancing-script', name: 'Dancing Script' },
  { id: 'great-vibes', name: 'Great Vibes' },
  { id: 'italianno', name: 'Italianno' },
  { id: 'satisfy', name: 'Satisfy' }
];

const PAPER_SIZES = [
  { id: 'a4', name: 'A4 (210 × 297 mm)', width: 210, height: 297 },
  { id: 'letter', name: 'US Letter (8.5 × 11 in)', width: 215.9, height: 279.4 }
];

const LINE_SPACING_OPTIONS = [
  { id: 'narrow', name: 'Narrow' },
  { id: 'normal', name: 'Normal' },
  { id: 'wide', name: 'Wide' }
];

const TEXT_STYLE_OPTIONS = [
  { id: 'solid', name: 'Solid' },
  { id: 'dotted', name: 'Dotted' },
  { id: 'dashed', name: 'Dashed' }
];

export default function NumberTracingWorksheet() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Content settings
  const [worksheetTitle, setWorksheetTitle] = useState('');

  // Font settings
  const [fontFamily, setFontFamily] = useState('writing-step');

  // Appearance settings
  const [lineColor, setLineColor] = useState('#cccccc');
  const [textStyle, setTextStyle] = useState('solid');
  const [textOpacity, setTextOpacity] = useState(20);
  const [textThickness, setTextThickness] = useState(1);
  const [textColor, setTextColor] = useState('#CCCCCC');

  // Page layout settings
  const [paperSize, setPaperSize] = useState('a4');
  const [orientation, setOrientation] = useState('tall');
  const [lineSpacing, setLineSpacing] = useState('normal');

  // Advanced settings
  const [showNameField, setShowNameField] = useState(true);
  const [nameFieldLabel, setNameFieldLabel] = useState('Name');
  const [showDateField, setShowDateField] = useState(true);
  const [dateFieldLabel, setDateFieldLabel] = useState('Date');

  const previewSettings = useMemo(() => ({
    worksheetTitle,
    font: fontFamily,
    lineColor,
    textStyle,
    textOpacity,
    textThickness,
    textColor,
    paperSize,
    orientation,
    lineSpacing,
    showNameField,
    nameFieldLabel,
    showDateField,
    dateFieldLabel
  }), [worksheetTitle, fontFamily, lineColor, textStyle, textOpacity, textThickness, textColor, paperSize, orientation, lineSpacing, showNameField, nameFieldLabel, showDateField, dateFieldLabel]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/worksheets/generate', {
        toolType: 'number-tracing',
        worksheetTitle,
        font: fontFamily,
        lineColor,
        textStyle,
        textOpacity,
        textThickness,
        textColor,
        paperSize,
        orientation,
        lineSpacing,
        showNameField,
        nameFieldLabel,
        showDateField,
        dateFieldLabel
      }, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bastion-number-tracing-worksheet.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      setError('Failed to generate worksheet. Please try again.');
      console.error('Generate error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <nav className="text-sm text-gray-600">
          <span className="hover:text-gray-700 cursor-pointer">Home</span>
          <span className="mx-2">&gt;</span>
          <span className="hover:text-gray-700 cursor-pointer">All Tools</span>
          <span className="mx-2">&gt;</span>
          <span className="text-bastion-black font-medium">Number Tracing Worksheets</span>
        </nav>
      </div>

      {/* Hero Section */}
      <div className="text-center py-8 px-4">
        <h1 className="text-4xl md:text-5xl font-bold text-bastion-black mb-4">
          Printable <span className="text-bastion-black">Number Tracing</span> Worksheets
        </h1>
        <p className="text-gray-700 max-w-2xl mx-auto mb-6">
          The essential tool for mastering number writing. Create and
          download personalized tracing number worksheets to build a strong foundation for
          math and writing.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <span className="px-4 py-2 bg-bastion-gray-200 text-bastion-black rounded-full text-sm font-medium">
            Numbers 0-9
          </span>
          <span className="px-4 py-2 bg-bastion-gray-200 text-bastion-black rounded-full text-sm font-medium">
            Customizable Settings
          </span>
          <span className="px-4 py-2 bg-bastion-gray-200 text-bastion-black rounded-full text-sm font-medium">
            Foundation Building
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <div className="space-y-6">
            {/* Title outside card like target */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Number Tracing Worksheet</h2>
              <p className="text-gray-600 text-sm">Create 0-9 number practice sheets (10 pages)</p>
            </div>

            {/* Content Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Content</h3>
              <p className="text-gray-600 text-sm mb-4">Fixed 10 pages (0-9), each number fills the entire page</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Worksheet Title</label>
                  <input
                    type="text"
                    value={worksheetTitle}
                    onChange={(e) => setWorksheetTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    placeholder="Enter worksheet title (optional)"
                    maxLength={50}
                  />
                  <p className="text-xs text-gray-500 mt-1">{worksheetTitle.length}/50 characters</p>
                </div>
              </div>
            </div>

            {/* Font Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Font</h3>
              <p className="text-gray-600 text-sm mb-4">Choose the handwriting font style</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                <FontSelect fonts={FONTS} value={fontFamily} onChange={setFontFamily} />
              </div>
            </div>

            {/* Appearance Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Appearance</h3>
              <p className="text-gray-600 text-sm mb-4">Customize colors, opacity, and text styling</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Line Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={lineColor}
                      onChange={(e) => setLineColor(e.target.value)}
                      className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={lineColor}
                      onChange={(e) => setLineColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Style</label>
                  <select
                    value={textStyle}
                    onChange={(e) => setTextStyle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  >
                    {TEXT_STYLE_OPTIONS.map(style => (
                      <option key={style.id} value={style.id}>{style.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Opacity</label>
                  <input
                    type="range"
                    min="20"
                    max="100"
                    value={textOpacity}
                    onChange={(e) => setTextOpacity(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-bastion-black"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>20%</span>
                    <span>25%</span>
                    <span>30%</span>
                    <span>35%</span>
                    <span>40%</span>
                    <span>45%</span>
                    <span>50%</span>
                    <span>55%</span>
                    <span>60%</span>
                    <span>65%</span>
                    <span>70%</span>
                    <span>75%</span>
                    <span>80%</span>
                    <span>85%</span>
                    <span>90%</span>
                    <span>95%</span>
                    <span>100%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Thickness</label>
                  <input
                    type="range"
                    min="0.25"
                    max="2"
                    step="0.25"
                    value={textThickness}
                    onChange={(e) => setTextThickness(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-bastion-black"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0.25</span>
                    <span>0.5</span>
                    <span>0.75</span>
                    <span>1</span>
                    <span>1.25</span>
                    <span>1.5</span>
                    <span>1.75</span>
                    <span>2</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Page Layout Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Page Layout</h3>
              <p className="text-gray-600 text-sm mb-4">Configure paper size, orientation, and spacing</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paper Size</label>
                  <select
                    value={paperSize}
                    onChange={(e) => setPaperSize(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  >
                    {PAPER_SIZES.map(size => (
                      <option key={size.id} value={size.id}>{size.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Orientation</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="orientation"
                        value="tall"
                        checked={orientation === 'tall'}
                        onChange={(e) => setOrientation(e.target.value)}
                        className="w-4 h-4 text-bastion-black focus:ring-bastion-black"
                      />
                      <span className="text-sm">Tall</span>
                      <svg className="w-4 h-6" viewBox="0 0 16 24" fill="currentColor">
                        <rect x="1" y="1" width="14" height="22" rx="1" stroke="currentColor" strokeWidth="1" fill="none" />
                        <line x1="3" y1="4" x2="13" y2="4" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="3" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="3" y1="10" x2="13" y2="10" stroke="currentColor" strokeWidth="0.5" />
                      </svg>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="orientation"
                        value="wide"
                        checked={orientation === 'wide'}
                        onChange={(e) => setOrientation(e.target.value)}
                        className="w-4 h-4 text-bastion-black focus:ring-bastion-black"
                      />
                      <span className="text-sm">Wide</span>
                      <svg className="w-6 h-4" viewBox="0 0 24 16" fill="currentColor">
                        <rect x="1" y="1" width="22" height="14" rx="1" stroke="currentColor" strokeWidth="1" fill="none" />
                        <line x1="3" y1="4" x2="21" y2="4" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="3" y1="7" x2="21" y2="7" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="0.5" />
                      </svg>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Line Spacing</label>
                  <select
                    value={lineSpacing}
                    onChange={(e) => setLineSpacing(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  >
                    {LINE_SPACING_OPTIONS.map(spacing => (
                      <option key={spacing.id} value={spacing.id}>{spacing.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Advanced Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Advanced</h3>
              <p className="text-gray-600 text-sm mb-4">Additional settings and practice modes</p>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Show Name Field</label>
                  <button
                    type="button"
                    onClick={() => setShowNameField(!showNameField)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showNameField ? 'bg-bastion-black' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showNameField ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {showNameField && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name Field Label</label>
                    <input
                      type="text"
                      value={nameFieldLabel}
                      onChange={(e) => setNameFieldLabel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Show Date Field</label>
                  <button
                    type="button"
                    onClick={() => setShowDateField(!showDateField)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showDateField ? 'bg-bastion-black' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showDateField ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {showDateField && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Field Label</label>
                    <input
                      type="text"
                      value={dateFieldLabel}
                      onChange={(e) => setDateFieldLabel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full py-4 bg-bastion-black hover:bg-bastion-primary-hover text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Generate PDF
                </>
              )}
            </button>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
          </div>

          {/* Preview Panel */}
          <PdfPreview toolType="number-tracing" settings={previewSettings} pageCount={10} />
        </div>

        {/* AI Analysis CTA */}
        <div className="mt-12 bg-bastion-gray-100 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Want personalized feedback on your handwriting?
          </h2>
          <p className="text-gray-700 mb-6">
            Try our AI-powered handwriting analysis tool. Get instant feedback on legibility,
            consistency, spacing, and more to improve your writing skills.
          </p>
          <a
            href="/analysis"
            className="inline-flex items-center gap-2 px-6 py-3 bg-bastion-black hover:bg-bastion-primary-hover text-white font-semibold rounded-xl transition-all"
          >
            Try AI Handwriting Analysis
            <span className="bg-white/30 text-xs px-2 py-0.5 rounded-full">Free first try</span>
          </a>
        </div>

        <ToolPageSections toolType="number-tracing" />
      </div>
    </div>
  );
}
